import React, { useMemo } from "react";
import { Collapse, Space } from "antd";
import styled from "styled-components";

const SectionsCollapse = styled(Collapse)`
  background: transparent;

  .ant-collapse-item {
    margin-bottom: 12px;
    border: 1px solid #f0f0f0;
    border-radius: 8px !important;
    overflow: hidden;
    background: #fff;
  }

  .ant-collapse-header {
    align-items: center !important;
    padding: 12px 16px !important;
  }

  .ant-collapse-content-box {
    padding: 8px 16px 16px !important;
  }
`;

const SectionTitle = styled.span`
  font-weight: 600;
`;

/**
 * ExpandableSections — reusable accordion/collapse for long admin forms.
 *
 * @param {object} props
 * @param {{ key: string, title: React.ReactNode, extra?: React.ReactNode, children: React.ReactNode, forceRender?: boolean }[]} props.sections
 * @param {string[]} [props.defaultOpenKeys] - Section keys open on first render
 * @param {string[]} [props.activeKeys] - Controlled open keys
 * @param {(keys: string|string[]) => void} [props.onChange]
 * @param {boolean} [props.accordion] - Only one section open at a time
 * @param {boolean} [props.bordered]
 * @param {string} [props.className]
 * @param {React.CSSProperties} [props.style]
 */
const ExpandableSections = ({
  sections = [],
  defaultOpenKeys,
  activeKeys,
  onChange,
  accordion = false,
  bordered = false,
  className,
  style,
}) => {
  const items = useMemo(
    () =>
      (sections || [])
        .filter((section) => section && section.key != null)
        .map((section) => ({
          key: String(section.key),
          label: (
            <Space size="small" align="center" wrap>
              <SectionTitle>{section.title}</SectionTitle>
              {section.extra || null}
            </Space>
          ),
          children: section.children,
          forceRender: section.forceRender === true,
        })),
    [sections]
  );

  const collapseProps = {
    accordion,
    bordered,
    className,
    style,
    items,
    onChange,
  };

  if (activeKeys != null) {
    collapseProps.activeKey = activeKeys;
  } else if (defaultOpenKeys != null) {
    collapseProps.defaultActiveKey = defaultOpenKeys;
  }

  return <SectionsCollapse {...collapseProps} />;
};

export default ExpandableSections;
